/**
 * CheckButtonGroupUI - 单选按钮组组件
 * 提供可配置的单选按钮组，支持水平/垂直排列、图标+文字或仅图标模式
 */

import { getI18nText } from '@/utils/commonFunc';
import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';

export interface CheckButtonItem {
    /** 按钮ID */
    id: string;
    /** 按钮标题 */
    title: string;
    /** 按钮值 */
    value: string;
    /** 图标名称 */
    iconName?: string;
    /** 点击事件回调，返回 false 时阻止激活按钮 */
    onClickEvent: (item: CheckButtonItem, index: number) => boolean | void;
    /** 是否只显示图标 */
    iconOnly?: boolean;
    /** 是否禁用 */
    disabled?: boolean;
    /** 自定义样式类 */
    className?: string;
    /** 多语言键 */
    i18nKey?: string;
    /** 标题多语言键 */
    titleI18nKey?: string;
}

export interface CheckButtonGroupOptions extends ElementOptions {
    /** 按钮列表 */
    items: CheckButtonItem[];
    /** 排列方向 */
    direction?: 'horizontal' | 'vertical';
    containerClass:'',
    /** 按钮组样式类 */
    groupClass?: string;
    /** 按钮样式类 */
    buttonClass?: string;
    /** 激活状态样式类 */
    activeClass?: string;
    /** 默认激活的按钮ID */
    defaultActiveId?: string;
    /** 按钮间距 */
    gap?: string;
    /** 标签文本 */
    label?: string;
    /** 是否显示标签 */
    showLabel?: boolean;
    /** 标签位置 */
    labelPosition?: 'left' | 'right' | 'top' | 'bottom';
    /** 自定义标签样式类 */
    labelClass?: string;
    /** 多语言键 */
    i18nKey?: string;
    /** 图标名称 */
    iconName?: string;
    /** 标题多语言键 */
    titleI18nKey?: string;
}

export class CheckButtonGroupUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private options: CheckButtonGroupOptions;
    private items: CheckButtonItem[] = [];
    private activeId: string | null = null;
    private buttons: Map<string, HTMLElement> = new Map();
    private onClickCallbacks: ((item: CheckButtonItem, index: number) => void)[] = [];
    private labelElement: HTMLElement | null = null;

    constructor(options: CheckButtonGroupOptions) {
        this.ui = bootstrapUI;
        this.options = {
            direction: 'horizontal',
            groupClass: 'check-button-group',
            buttonClass: 'check-button',
            activeClass: 'active',
            showLabel: true,
            labelPosition: 'left',
            labelClass: 'check-button-group-label',
            ...options
        };

        this.items = this.options.items || [];
        this.container = this.createContainer();
        this.setupEventListeners();
        this.populateButtons();
        
        // 设置默认激活按钮
        if (this.options.defaultActiveId) {
            this.setActiveButton(this.options.defaultActiveId);
        }
    }

    private createContainer(): HTMLElement {
        let labeli18nKey = '';
        if(this.options.i18nKey){
            labeli18nKey = this.options.i18nKey;
            this.options.i18nKey = '';
        }
        const container = this.ui.createElement({
            className: [
                'check-button-group-container',
                ...(this.options.containerClass ? [this.options.containerClass] : []),
                `check-button-group-label-${this.options.labelPosition || 'left'}`,
                ...(Array.isArray(this.options.className) ? this.options.className : this.options.className ? [this.options.className] : [])
            ],
            styles: {
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }
        });

        // 创建标签（如果启用）
        if (this.options.showLabel && this.options.label) {
            const label = this.ui.createElement({
                tag: 'label',
                className: [
                    this.options.labelClass || 'check-button-group-label'
                ],
                textContent: this.options.label,
                styles: {
                    margin: '0',
                    whiteSpace: 'nowrap'
                },
                i18nKey: labeli18nKey
            });
            this.labelElement = label;
            container.appendChild(label);
        }

        // 创建按钮组容器
        const buttonGroupContainer = this.ui.createElement({
            className: [
                'check-button-group',
                ...(this.options.groupClass ? [this.options.groupClass] : []),
                this.options.direction === 'vertical' ? 'vertical' : 'horizontal'
            ],
            styles: {
                gap: this.options.gap || '0',
                display: 'flex'
            }
        });

        container.appendChild(buttonGroupContainer);

        return container;
    }

    private setupEventListeners(): void {
        // 可以在这里添加全局事件监听器
    }

    private populateButtons(): void {
        // 找到按钮组容器
        const buttonGroupContainer = this.container.querySelector('.check-button-group') as HTMLElement;
        if (buttonGroupContainer) {
            buttonGroupContainer.innerHTML = '';
        }
        this.buttons.clear();

        this.items.forEach((item, index) => {
            const buttonElement = this.createButtonElement(item, index);
            this.buttons.set(item.id, buttonElement);
            if (buttonGroupContainer) {
                buttonGroupContainer.appendChild(buttonElement);
            }
        });
    }

    private createButtonElement(item: CheckButtonItem, index: number): HTMLElement {
        const button = this.ui.createElement({
            tag: 'button',
            className: [
                this.options.buttonClass || 'check-button',
                ...(item.iconOnly ? ['icon-only'] : []),
                ...(this.options.direction === 'vertical' ? ['vertical'] : []),
                ...(item.className ? [item.className] : []),
                ...(item.disabled ? ['disabled'] : [])
            ],
            textContent: '', // 清空默认文本内容
            attributes: {
                type: 'button',
                'data-id': item.id,
                'data-index': index.toString(),
                'aria-pressed': 'false'
            },
            // i18nKey: item.i18nKey
        });

        // 添加图标（如果有）
        if (item.iconName) {
            const icon = this.ui.createIcon({
                iconName: item.iconName,
                size: 'sm',
                className: 'check-button-icon'
            });
            button.appendChild(icon);
        }

        // 添加标题（如果不是仅图标模式）
        if (!item.iconOnly && item.title) {
            // const titleSpan = this.ui.createElement({
            //     tag: 'div',
            //     textContent: item.title,
            //     className: 'check-button-title'
            // });
            // if(item.titleI18nKey){
            //     titleSpan.setAttribute('data-i18n-title', item.titleI18nKey);
            // }
            // button.appendChild(titleSpan);
            if(item.titleI18nKey){  
                button.setAttribute('title', getI18nText(item.titleI18nKey));
                button.setAttribute('data-i18n-title', item.titleI18nKey);
            }
        }

        if(item.i18nKey){
            let namespan:HTMLElement=button.querySelector('.check-button-title') as HTMLElement;
            if(!namespan){
                namespan=document.createElement('div');
            }
            namespan.textContent=item.title;
            if(item.titleI18nKey){
                namespan.setAttribute('data-i18n-title', item.titleI18nKey);
            }
            namespan.setAttribute('data-i18n', item.i18nKey);
            button.appendChild(namespan);
        }

        // 添加点击事件
        if (!item.disabled) {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                this.handleButtonClick(item, index);
            });
        }

        // 悬停效果现在由 CSS 处理

        return button;
    }

    private handleButtonClick(item: CheckButtonItem, index: number): void {
        if(this.activeId === item.id){
            return;
        }

        // 执行按钮回调并获取返回值
        let shouldActivate = true;
        if (item.onClickEvent) {
            const result = item.onClickEvent(item, index);
            // 如果回调返回 false，则不激活按钮
            if (result === false) {
                shouldActivate = false;
            }
        }

        // 执行全局回调
        this.onClickCallbacks.forEach(callback => callback(item, index));
        
        // 根据回调返回值决定是否设置当前按钮为激活状态
        if (shouldActivate) {
            this.setActiveButton(item.id);
        }
    }

    /**
     * 设置组件激活状态（启用/禁用整个组件）
     */
    public setActive(status: boolean): void {
        if (!status) {
            // 禁用整个组件
            this.container.classList.add('forbidden-event-dom');
            
            // 禁用所有按钮
            this.buttons.forEach((button, buttonId) => {
                this.setDisabled(buttonId, true);
            });
        } else {
            // 启用整个组件
            this.container.classList.remove('forbidden-event-dom');
            
            // 启用所有按钮
            this.buttons.forEach((button, buttonId) => {
                this.setDisabled(buttonId, false);
            });
        }
    }

    /**
     * 设置激活按钮
     */
    public setActiveButton(id: string): boolean {
        const item = this.items.find(item => item.id === id);
        if (!item || item.disabled) {
            return false;
        }

        // 取消所有按钮的激活状态
        this.buttons.forEach((button, buttonId) => {
            this.deactivateButton(buttonId);
        });

        // 激活指定按钮
        this.activateButton(id);
        this.activeId = id;

        return true;
    }

    /**
     * 取消激活按钮
     */
    public deactivate(id: string): boolean {
        if (this.activeId === id) {
            this.deactivateButton(id);
            this.activeId = null;
            return true;
        }
        return false;
    }

    /**
     * 设置按钮禁用状态
     */
    public setDisabled(id: string, disabled: boolean): boolean {
        const item = this.items.find(item => item.id === id);
        const button = this.buttons.get(id);
        
        if (!item || !button) {
            return false;
        }

        item.disabled = disabled;
        
        if (disabled) {
            button.classList.add('disabled', 'opacity-50');
            button.style.cursor = 'not-allowed';
            button.setAttribute('disabled', 'true');
            
            // 如果当前激活的按钮被禁用，取消激活
            if (this.activeId === id) {
                this.activeId = null;
            }
        } else {
            button.classList.remove('disabled', 'opacity-50');
            button.style.cursor = 'pointer';
            button.removeAttribute('disabled');
        }

        return true;
    }

    private activateButton(id: string): void {
        const button = this.buttons.get(id);
        if (button) {
            button.classList.add(this.options.activeClass || 'active');
            button.setAttribute('aria-pressed', 'true');
        }
    }

    private deactivateButton(id: string): void {
        const button = this.buttons.get(id);
        if (button) {
            button.classList.remove(this.options.activeClass || 'active');
            button.setAttribute('aria-pressed', 'false');
        }
    }

    /**
     * 获取当前激活的按钮ID
     */
    public getActiveId(): string | null {
        return this.activeId;
    }

    /**
     * 获取当前激活的按钮项
     */
    public getActiveItem(): CheckButtonItem | null {
        return this.items.find(item => item.id === this.activeId) || null;
    }

    /**
     * 添加点击回调
     */
    public onButtonClick(callback: (item: CheckButtonItem, index: number) => void): void {
        this.onClickCallbacks.push(callback);
    }

    /**
     * 获取所有按钮项
     */
    public getItems(): CheckButtonItem[] {
        return [...this.items];
    }

    /**
     * 获取容器元素
     */
    public getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 获取标签文本
     */
    public getLabel(): string {
        return this.options.label || '';
    }

    /**
     * 设置标签文本
     */
    public setLabel(label: string): void {
        this.options.label = label;
        if (this.labelElement) {
            this.labelElement.textContent = label;
        } else if (label && this.options.showLabel) {
            // 如果标签元素不存在但需要设置标签，创建它
            const labelElement = this.ui.createElement({
                tag: 'label',
                className: [
                    this.options.labelClass || 'check-button-group-label'
                ],
                textContent: label
            });
            this.labelElement = labelElement;
            // 插入到按钮组容器之前
            const buttonGroupContainer = this.container.querySelector('.check-button-group');
            if (buttonGroupContainer) {
                this.container.insertBefore(labelElement, buttonGroupContainer);
            }
        }
    }

    /**
     * 设置是否显示标签
     */
    public setShowLabel(showLabel: boolean): void {
        this.options.showLabel = showLabel;
        if (showLabel && this.options.label) {
            if (!this.labelElement) {
                this.setLabel(this.options.label);
            }
        } else if (this.labelElement) {
            this.labelElement.remove();
            this.labelElement = null;
        }
    }

    /**
     * 设置标签位置
     */
    public setLabelPosition(position: 'left' | 'right' | 'top' | 'bottom'): void {
        this.options.labelPosition = position;
        // 移除旧的位置类
        this.container.classList.remove('check-button-group-label-left', 'check-button-group-label-right', 'check-button-group-label-top', 'check-button-group-label-bottom');
        // 添加新的位置类
        this.container.classList.add(`check-button-group-label-${position}`);
        
        // 根据位置调整容器布局
        if (position === 'top' || position === 'bottom') {
            this.container.style.flexDirection = position === 'top' ? 'column' : 'column-reverse';
            this.container.style.alignItems = 'flex-start';
        } else {
            this.container.style.flexDirection = position === 'left' ? 'row' : 'row-reverse';
            this.container.style.alignItems = 'center';
        }
    }

    /**
     * 销毁组件
     */
    public destroy(): void {
        this.onClickCallbacks = [];
        this.buttons.clear();
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}




